// Product category inference from a zh-TW product name.
//
// WHY: the receiving search box matches free text against the product name, so
// searching 「上衣」 surfaces every 短袖上衣 AND every 落地褲 whose name happens to
// share a token (涼感 / 寬版 / 針織…). Warehouse staff pick the category first,
// then keyword-search inside it.
//
// HOW: Chinese product names are head-final — the noun that says what the thing
// IS sits at the END (「破洞針織外套」 = jacket, not knitwear; 「連帽短袖套裝」 = set,
// not a tee). So we score every keyword by where it ENDS in the name and let the
// right-most one win, tie-broken by keyword length (more specific wins).
//
// There is no category column anywhere in the order pipeline (Shopee / Shopify /
// storefront line items all carry only a name + variant string), so this is a
// name classifier by necessity, not by preference.

export type CategoryKey = 'top' | 'outer' | 'bottom' | 'set' | 'shoes' | 'acc' | 'beauty' | 'other';

export interface CategoryDef {
  key: CategoryKey;
  label: string;
  /** Lower-cased substrings. Matched against the lower-cased product name. */
  keywords: string[];
}

// Order here is the chip display order.
export const CATEGORY_DEFS: CategoryDef[] = [
  {
    key: 'top',
    label: '上衣',
    keywords: [
      '上衣', '襯衫', 'polo', 't恤', 't卹', 'tee', '短袖', '長袖', '無袖', '五分袖',
      '針織', '帽t', '大學t', '衛衣', '背心', '開襟', '開衫', '毛衣', '內搭', '衫',
      '打底', '短t', '長t',
    ],
  },
  {
    key: 'outer',
    label: '外套',
    keywords: ['外套', '夾克', '大衣', '風衣', '羽絨', '皮衣', '斗篷', '披肩', '飛行外套'],
  },
  {
    key: 'bottom',
    label: '下身',
    // 褲 covers 短褲/西裝褲/牛仔褲/落地褲/寬褲…; 裙 covers 短裙/長裙/褲裙.
    keywords: ['褲', '裙'],
  },
  {
    key: 'set',
    label: '套裝',
    keywords: ['套裝', '兩件式', '上下套'],
  },
  {
    key: 'shoes',
    label: '鞋',
    keywords: ['鞋', '靴', '拖鞋', '夾腳拖', '樂福', '德比'],
  },
  {
    key: 'acc',
    label: '配件',
    // NOTE: bare 鍊 is deliberately absent — 拉鍊 (zipper) appears in garment names.
    keywords: [
      '包', '帽', '襪', '皮帶', '腰帶', '皮夾', '錢包', '卡夾', '手錶', '錶',
      // 手練 is a recurring typo for 手鍊 in the live catalog — matched on purpose.
      '項鍊', '手鍊', '手練', '戒指', '手環', '耳環', '耳釘', '胸針', '別針', '吊飾',
      '眼鏡', '墨鏡', '領帶', '領結', '圍巾', '絲巾', '手套', '髮帶', '鑰匙圈',
    ],
  },
  {
    key: 'beauty',
    label: '保養',
    keywords: [
      '香水', '香精', '香氛', '防曬', '洗面乳', '清潔乳', '卸妝', '刮鬍', '精華',
      '乳液', '爽膚水', '化妝水', '保養露', '素顏霜', '妝前乳', '遮瑕', '氣墊',
      '粉底', 'bb霜', 'bb粉', '護唇', '唇膏', '眼霜', '面膜', '噴霧', '濕紙巾',
      '面紙', '洗髮', '沐浴', '身體乳', '控油', '毛孔', '爽膚', '護膚',
      // 棉片 / 淨膚 / 禮盒 exist only on MdoC lines in this catalog.
      '棉片', '淨膚', '禮盒',
    ],
  },
];

export const CATEGORY_LABELS: Record<CategoryKey, string> = {
  top: '上衣', outer: '外套', bottom: '下身', set: '套裝',
  shoes: '鞋', acc: '配件', beauty: '保養', other: '其他',
};

// Product names repeat heavily across orders (one style → many lines), so the
// per-name result is cached; classification runs inside filter loops.
const cache = new Map<string, CategoryKey>();

export function classifyProductName(rawName: string): CategoryKey {
  if (!rawName) return 'other';
  const cached = cache.get(rawName);
  if (cached) return cached;

  const name = rawName.toLowerCase();
  let bestKey: CategoryKey = 'other';
  let bestEnd = -1;
  let bestLen = 0;

  for (const def of CATEGORY_DEFS) {
    for (const kw of def.keywords) {
      const at = name.lastIndexOf(kw);
      if (at < 0) continue;
      const end = at + kw.length;
      // Right-most head noun wins; on a tie the longer (more specific) keyword wins.
      if (end > bestEnd || (end === bestEnd && kw.length > bestLen)) {
        bestEnd = end;
        bestLen = kw.length;
        bestKey = def.key;
      }
    }
  }

  cache.set(rawName, bestKey);
  return bestKey;
}
